import { DateTime } from 'luxon';

export const CANONICAL_TIMEZONE = 'UTC' as const;

/**
 * Utility function to get the user's browser timezone.
 * @returns The user's local timezone as a string (e.g., 'America/New_York', 'Asia/Tokyo').
 */
export const getUserTimeZone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Parse string or Date input into Luxon DateTime (auto-detects format)
 */
export const parseToDateTime = (
  input?: string | Date | null
): DateTime | null => {
  if (!input) return null;

  if (input instanceof Date) {
    return DateTime.fromJSDate(input, { zone: CANONICAL_TIMEZONE });
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();

    // Date-only string
    let dt = DateTime.fromFormat(trimmed, 'yyyy-MM-dd', {
      zone: CANONICAL_TIMEZONE
    });
    if (dt.isValid) return dt;

    // ISO-like string with timezone offset
    dt = DateTime.fromISO(trimmed, { setZone: true });
    if (dt.isValid) return dt;

    // SQL-like string
    dt = DateTime.fromSQL(trimmed, { setZone: true });
    if (dt.isValid) return dt;
  }

  return null;
};

/**
 * Converts input to the desired timezone and locale-aware format.
 *
 * @param input - String or Date input to convert
 * @param format - Optional format string. If provided, used exactly as-is
 * @param zone - Optional timezone. Defaults to browser timezone
 * @param locale - Output locale. Defaults to browser locale
 *
 * Default behavior (if no format provided):
 *   - English: "MMMM dd, yyyy hh:mm a" (US-style)
 *   - Other locales: standard local format
 */
export const convertToLocal = (
  input?: string | Date | null,
  format?: string,
  zone?: string,
  locale?: string
): string => {
  if (!input) return '';

  const dt = parseToDateTime(input);
  if (!dt) return '';

  const userZone = zone ?? getUserTimeZone();
  const userLocale = locale ?? navigator.language;
  const converted = dt.setZone(userZone).setLocale(userLocale);

  // Return in custom format if provided
  if (format) {
    return converted.toFormat(format);
  }

  // For locales, use Luxon's locale-aware date/time format
  return converted.toLocaleString({
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: undefined // Luxon picks 12/24 based on locale
  });
};
